/* ==========================================================================
   MP SIGNUP PILL — behaviour
   --------------------------------------------------------------------------
   Companion files:
     sections/mp-signup-pill.liquid  (markup + schema, writes the config)
     assets/mp-signup-pill.css       (all presentation)

   All configuration arrives as data-* attributes on the root element. This
   file reads no theme settings and no globals other than the Klaviyo onsite
   script it may need to sniff a public company id from.

   SIGNUP GOES TO TWO PLACES, deliberately:

     1. Klaviyo, via the public client subscriptions endpoint. This is the
        same endpoint Klaviyo's own embedded forms use, it is CORS-open for
        browser use, and it needs no secret — the company id is public and
        already present in every page of this site. Landing on the list is
        what fires the live Welcome Series flow, so this is the call that
        actually delivers the discount email. It is AWAITED; success or
        failure of the whole submit is decided by it.

     2. Shopify, via the native customer form endpoint — the same /contact
        endpoint and the same form_type the theme's footer newsletter form
        has always posted to. This guarantees a Shopify CUSTOMER record with
        email marketing consent rather than depending on Klaviyo's
        sync-back-to-Shopify setting being switched on. After a Klaviyo
        success it is fire-and-forget: Shopify may answer a scripted form
        POST with a captcha challenge, and that must never fail a signup
        that has already landed in Klaviyo.

   AND IT IS ALSO THE FALLBACK (added 2026-09-28). When the Klaviyo call
   never leaves the device — a content blocker, offline, a refused preflight
   — call 2 is retried AWAITED, and its verdict decides the submit. It works
   because it goes to the store's own domain, which no blocklist touches.

   Why that case is real and not defensive programming: Vivaldi and the
   DuckDuckGo browser ship DuckDuckGo's Tracker Radar list, where klaviyo.com
   is "default": "block" with zero exception rules, so every klaviyo.com
   request dies before it is sent. Klaviyo's own popup does not survive it
   either — measured on the live theme the same day, 41 Klaviyo form nodes
   render unblocked and 0 render blocked, so those shoppers see no form at
   all and nobody ever reports it. This one is first-party markup, so it is
   still on screen and can still capture the address.

   Only a request that NEVER COMPLETED earns the fallback. A Klaviyo answer
   of 400 is an answer, and the shopper sees the error.

   Verified 2026-09-16: the Klaviyo call returns 202 and the profile lands
   with consent SUBSCRIBED (tested against a throwaway list, since deleted).
   ========================================================================== */

(function () {
  'use strict';

  var VERSION = 'v14';
  var STORE_DISMISSED = 'mp-signup-pill:dismissed';
  var STORE_SUBSCRIBED = 'mp-signup-pill:subscribed';
  var STORE_SNOOZED = 'mp-signup-pill:snoozed';
  /* Per-tab, so "Viewed" is counted once a session the way Klaviyo's own
     form counts it. In localStorage it would be once per browser forever and
     the submit rate would climb every month without anything improving. */
  var SESSION_VIEWED = 'mp-signup-pill:viewed';
  /* Auto-expand has fired. Written to whichever store the scope names,
     exactly like the snooze: sessionStorage for 'session' (dies with the
     tab, which is the reset Monkey asked for) and localStorage for
     'hours' (outlives the tab, compared against a timestamp). */
  var STORE_AUTOEXPANDED = 'mp-signup-pill:autoexpanded';

  /* THREE WAYS OUT, and they are deliberately not the same thing:

       X          -> snoozed. The card slides to the edge of the screen and
                     leaves a tab behind; still there, out of the way, and
                     one tap from coming back. The shopper said not now.
       opt-out    -> dismissed. Gone, for the dismissal window. The shopper
                     said no, in words, on purpose.
       ESC        -> collapsed. Nothing is remembered at all; an accidental
                     keypress must not cost anyone the offer.

     CHANGED 2026-10-07 on Monkey's ask. The X used to dismiss and a separate
     minus button used to snooze; now the X snoozes and the minus button is
     gone. The reasoning is worth keeping: an X is a universal "make this go
     away" and nothing about the glyph says whether the shopper has declined
     10% off or merely parked it, so the hard answer was moved onto a line of
     text that says what it does. Two consequences follow and both are wanted:

       - Signup Form Closed now counts rejection rather than "not now". It
         fires from the opt-out and from nothing else.
       - Tapping the X no longer costs the shopper the offer for
         dismiss_days. It lasts the browsing session, like any other snooze.

     With allow_snooze OFF there is no snooze to route to, so the X keeps its
     old dismissing behaviour — see the close binding.

     Snooze is a separate key with its own window rather than a short
     dismissal, because the two have to coexist: a shopper can snooze, and
     then opt out from the reopened card, and the opt-out has to win.

     WHERE EACH ONE IS STORED, and why they are not the same place
     (changed 2026-10-07 on Monkey's ask):

       dismissed  -> localStorage. "No" should outlive the tab.
       subscribed -> localStorage. Forever. Never re-ask someone who gave
                     us their address.
       snoozed    -> sessionStorage by default, so "not now" lasts exactly
                     as long as the browsing session and is gone when the
                     tab closes. A merchant who wants it to outlive the tab
                     switches the scope setting to hours, which puts it back
                     in localStorage with the old timestamp window.

     NONE of the three is gated on the "Remember that it was closed" setting
     any more. That switch exists so the merchant can preview the pill
     repeatedly without clearing storage; wiring minimise and subscribed to
     it meant that with the switch off a minimise was forgotten on the next
     page load, and — worse — someone who had just signed up got prompted
     again. Both were live on BUILD 1.0 before this change: the saved
     settings have the switch OFF. The switch now governs the dismissal
     only, which is what it is named after. */
  var KLAVIYO_ENDPOINT = 'https://a.klaviyo.com/client/subscriptions';
  var KLAVIYO_REVISION = '2026-07-15';

  /* ---------------------------------------------------------------------
     Storage helpers. Safari in private mode throws on localStorage.setItem
     rather than returning falsy, and an uncaught throw here would abort the
     submit handler AFTER the subscription already succeeded — the worst
     possible place to fail. Every access is wrapped.
     --------------------------------------------------------------------- */
  function readStore(key) {
    try {
      return window.localStorage.getItem(key);
    } catch (e) {
      return null;
    }
  }

  function writeStore(key, value) {
    try {
      window.localStorage.setItem(key, value);
    } catch (e) {
      /* no-op: a widget that cannot remember a dismissal is still a
         working widget, just a more annoying one. */
    }
  }

  function removeStore(key) {
    try {
      window.localStorage.removeItem(key);
    } catch (e) {
      /* see writeStore */
    }
  }

  /* The same three wrapped in sessionStorage. Same reason for the try/catch
     — Safari in private mode throws on write here too — and the same
     consequence if it throws uncaught, so it gets the same treatment rather
     than a shared helper taking the storage object as an argument: reading
     `window.sessionStorage` is itself what throws in some locked-down
     configurations, so the property access has to be inside the try. */
  function readSession(key) {
    try {
      return window.sessionStorage.getItem(key);
    } catch (e) {
      return null;
    }
  }

  function writeSession(key, value) {
    try {
      window.sessionStorage.setItem(key, value);
    } catch (e) {
      /* see writeStore */
    }
  }

  function removeSession(key) {
    try {
      window.sessionStorage.removeItem(key);
    } catch (e) {
      /* see writeStore */
    }
  }

  /* ---------------------------------------------------------------------
     The minimise record. Which storage it lives in depends on the scope
     setting, so every read/write/clear goes through these three rather than
     touching a storage directly.

     A clear ALWAYS clears both. The scope is a merchant setting that can
     change between two page loads of the same session, and a stale key in
     the storage that is no longer being read would otherwise park the pill
     at the edge with nothing able to clear it.
     --------------------------------------------------------------------- */
  function readSnooze(scope) {
    return scope === 'hours' ? readStore(STORE_SNOOZED) : readSession(STORE_SNOOZED);
  }

  function writeSnooze(scope, value) {
    if (scope === 'hours') writeStore(STORE_SNOOZED, value);
    else writeSession(STORE_SNOOZED, value);
  }

  function readScoped(key, scope) {
    return scope === 'hours' ? readStore(key) : readSession(key);
  }

  function writeScoped(key, scope, value) {
    if (scope === 'hours') writeStore(key, value);
    else writeSession(key, value);
  }

  function clearScoped(key) {
    removeStore(key);
    removeSession(key);
  }

  function clearSnooze() {
    removeStore(STORE_SNOOZED);
    removeSession(STORE_SNOOZED);
  }

  /* Find the Klaviyo public company id. The section setting wins; otherwise
     read it off the onsite script the Klaviyo app embed already injects,
     which keeps the id in exactly one place (the app) instead of two. */
  function resolveCompanyId(configured) {
    if (configured) return configured;
    var script = document.querySelector('script[src*="static.klaviyo.com/onsite/js/"]');
    if (!script) return '';
    var fromQuery = script.src.match(/[?&]company_id=([A-Za-z0-9]+)/);
    if (fromQuery) return fromQuery[1];
    /* Older embeds carry the id as a path segment: /onsite/js/<id>/klaviyo.js */
    var fromPath = script.src.match(/\/onsite\/js\/([A-Za-z0-9]+)\//);
    return fromPath ? fromPath[1] : '';
  }

  /* Deliberately permissive. Anything stricter rejects addresses that are
     legal and deliverable; Klaviyo is the real validator and it answers
     with a 400 we surface. This only exists to catch the obvious typo
     before we spend a round trip on it. */
  function looksLikeEmail(value) {
    return /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(value);
  }

  /* ---------------------------------------------------------------------
     E.164, because Klaviyo answers a malformed number with a 400 and not a
     202. North America only, which is the honest scope: this store ships
     from Victoria BC and the field is labelled for a 10-digit number.

     Accepted:
       10 digits                      -> +1XXXXXXXXXX
       11 digits beginning 1          -> +1XXXXXXXXXX
       anything already written +…    -> kept if it is 11-15 digits

     Rejected rather than guessed. A number we "fixed" into the wrong country
     code is worse than a shopper being asked to retype: the consent record
     would be attached to someone else's phone.
     --------------------------------------------------------------------- */
  function normalisePhone(raw) {
    var value = String(raw || '').trim();
    if (!value) return '';

    var hadPlus = value.charAt(0) === '+';
    var digits = value.replace(/[^0-9]/g, '');

    if (hadPlus) {
      return digits.length >= 11 && digits.length <= 15 ? '+' + digits : '';
    }
    if (digits.length === 10) return '+1' + digits;
    if (digits.length === 11 && digits.charAt(0) === '1') return '+' + digits;
    return '';
  }

  /* ---------------------------------------------------------------------
     (XXX) XXX-XXXX as it is typed.

     Presentation only. normalisePhone() above is still the single authority
     on what gets sent to Klaviyo, and it strips every separator this adds,
     so the mask cannot change what a shopper is subscribed with — only how
     it looks while they type.

     Separators appear as the digit that needs them arrives rather than
     eagerly, so there is never a trailing ") " for the shopper to reverse
     out of and the caret never has to sit in the middle of punctuation.

     A leading 1 is dropped once a second digit exists. No NANP area code
     begins with 0 or 1, so a 1 in the first position is always the country
     code — which makes a pasted 1-250-555-0123 format correctly instead of
     shifting every group by one. Waiting for the second digit means the key
     a shopper just pressed never vanishes under them.

     Returns null — "not mine" — for anything longer than ten digits rather
     than truncating it. Truncating is the dangerous option and it is worth
     spelling out why: a pasted UK number typed without a + is twelve digits,
     and a mask that cut it to ten would hand normalisePhone() a perfectly
     well-formed Canadian number belonging to a stranger. normalisePhone()
     rejects the over-length string on its own and the shopper is asked to
     retype, which is the outcome its own header argues for.
     --------------------------------------------------------------------- */
  function formatNanpPhone(raw) {
    var digits = String(raw || '').replace(/[^0-9]/g, '');
    if (digits.length > 1 && digits.charAt(0) === '1') digits = digits.slice(1);
    if (digits.length > 10) return null;

    if (!digits) return '';
    if (digits.length <= 3) return '(' + digits;
    if (digits.length <= 6) return '(' + digits.slice(0, 3) + ') ' + digits.slice(3);
    return '(' + digits.slice(0, 3) + ') ' + digits.slice(3, 6) + '-' + digits.slice(6);
  }

  function countDigits(value) {
    var match = String(value || '').match(/[0-9]/g);
    return match ? match.length : 0;
  }

  /* The onsite script Klaviyo's app embed already injects. It is an array
     until the script finishes loading and an object with .push afterwards,
     and both accept the same call — which is the whole reason Klaviyo
     publishes this pattern. Guarded anyway: a content blocker removes the
     script entirely, and an analytics call must never be the thing that
     breaks a signup. */
  function trackKlaviyo(enabled, event, props) {
    if (!enabled) return;
    try {
      var k = window.klaviyo;
      if (!k || typeof k.push !== 'function') return;
      k.push(['track', event, props]);
    } catch (e) {
      /* An analytics failure is not a shopper-facing failure. */
    }
  }

  function MPSignupPill(root) {
    this.root = root;
    this.toggle = root.querySelector('[data-mp-pill-toggle]');
    this.card = root.querySelector('[data-mp-pill-card]');
    this.form = root.querySelector('[data-mp-pill-form]');
    this.input = root.querySelector('[data-mp-pill-input]');
    this.submit = root.querySelector('[data-mp-pill-submit]');
    this.errorEl = root.querySelector('[data-mp-pill-error]');
    this.formState = root.querySelector('[data-mp-pill-state-form]');
    this.successState = root.querySelector('[data-mp-pill-state-success]');
    this.badge = root.querySelector('[data-mp-pill-badge]');
    this.optout = root.querySelector('[data-mp-pill-optout]');
    this.smsState = root.querySelector('[data-mp-pill-state-sms]');
    this.smsForm = root.querySelector('[data-mp-pill-sms-form]');
    this.phone = root.querySelector('[data-mp-pill-phone]');
    this.smsSubmit = root.querySelector('[data-mp-pill-sms-submit]');
    this.smsSkip = root.querySelector('[data-mp-pill-sms-skip]');
    this.smsErrorEl = root.querySelector('[data-mp-pill-sms-error]');

    var d = root.dataset;
    this.config = {
      appearOn: d.mpAppearOn || 'load',
      scrollThreshold: parseFloat(d.mpScrollThreshold) || 25,
      appearDelay: parseFloat(d.mpAppearDelay) || 0,
      dismissDays: parseFloat(d.mpDismissDays) || 0,
      /* Absent attribute means remember. Only an explicit 'false' opts out,
         so a cached copy of the markup from before this setting existed
         keeps the safe behaviour rather than nagging every shopper. */
      remember: d.mpRemember !== 'false',
      wobble: d.mpWobble === 'true',
      wobbleAngle: parseFloat(d.mpWobbleAngle) || 6,
      wobbleInterval: (parseFloat(d.mpWobbleInterval) || 3) * 1000,
      wobbleOnHover: d.mpWobbleOnHover === 'true',
      successAutoclose: (parseFloat(d.mpSuccessAutoclose) || 0) * 1000,
      listId: d.mpKlaviyoList || '',
      /* The step only exists if the markup for it does. A merchant can turn
         the setting off, and then there is nothing to show whatever this
         says. */
      smsStep: d.mpSmsStep === 'true' && !!root.querySelector('[data-mp-pill-state-sms]'),
      smsListId: d.mpKlaviyoSmsList || '',
      customSource: d.mpCustomSource || 'MP Signup Pill',
      signupSource: d.mpSignupSource || 'site-flyout-v1',
      trackEvents: d.mpTrackEvents !== 'false',
      companyId: resolveCompanyId(d.mpKlaviyoCompany || ''),
      corner: d.mpCorner || 'bottom-right',
      allowSnooze: d.mpAllowSnooze === 'true',
      /* 'session' (the default) or 'hours'. Absent attribute means session,
         which is the behaviour Monkey asked for; a cached copy of the older
         markup therefore gets the new behaviour rather than no persistence
         at all, because the old code read the hours window through the
         remember flag and that flag is off on this store. */
      snoozeScope: d.mpSnoozeScope === 'hours' ? 'hours' : 'session',
      /* Auto-expand. Absent attribute means OFF, deliberately the opposite
         of the remember flag above: a browser holding a cached copy of the
         pre-v10 markup must not start opening itself unasked. The setting
         ships on, so the only readers who get the off path are stale ones. */
      autoExpand: d.mpAutoExpand === 'true',
      autoExpandThreshold: parseFloat(d.mpAutoExpandThreshold) || 50,
      autoExpandScope: d.mpAutoExpandScope === 'hours' ? 'hours' : 'session',
      autoExpandHours: parseFloat(d.mpAutoExpandHours) || 1,
      /* Where the open card sits under 750px. 'top' pins it to the top of
         the VISUAL viewport so an opening keyboard cannot shove it off the
         screen; 'corner' keeps the desktop behaviour. */
      mobileOpen: d.mpMobileOpen === 'top' ? 'top' : 'corner',
      snoozeHours: parseFloat(d.mpSnoozeHours) || 24,
      snoozeWobble: d.mpSnoozeWobble !== 'false',
      /* Seconds in the setting, ms here. Floored at 10s: anything faster on
         a parked edge tab reads as a glitch rather than a nudge. */
      snoozeWobbleInterval: Math.max(10, parseFloat(d.mpSnoozeWobbleInterval) || 60) * 1000,
      /* Mirrors --mp-pill-anim-duration. Needed in JS so the nudge's hold
         can outlast the CSS transition it triggers. */
      animDuration: parseFloat(d.mpAnimDuration) || 300,
      swipe: d.mpSwipe === 'true',
      createCustomer: d.mpCreateCustomer === 'true',
      customerTags: d.mpCustomerTags || 'newsletter',
      designPreview: d.mpDesignPreview || 'collapsed'
    };

    /* Both messages are captured once, here, and every showError() call
       names which one it wants. The earlier version passed '' to mean
       "keep whatever is already in there", which meant a validation
       message stayed on screen through a later network failure and told
       the shopper their address was malformed when it was not. */
    this.messages = {
      invalid: this.root.dataset.mpInvalidText || '',
      generic: this.errorEl ? this.errorEl.textContent.trim() : '',
      phoneInvalid: this.root.dataset.mpSmsInvalidText || ''
    };

    this.isOpen = false;
    this.step = 1;
    this.isSnoozed = false;
    this.isSubmitting = false;
    this.drag = null;
    this.suppressClick = false;
    this.suppressClickTimer = null;
    /* Which way "away" is. Right-hand corners slide right, left-hand
       corners slide left; the pill always leaves by its own nearest edge. */
    this.awaySign = this.config.corner.indexOf('left') > -1 ? -1 : 1;
    this.wobbleTimer = null;
    this.wobbleDelayTimer = null;
    this.scrollTimer = null;
    this.autoScrollTimer = null;
    this.onAutoScroll = null;
    this.resizeTimer = null;
    this.pinnedWidth = 0;
    this.reduceMotion =
      window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    this.pinViewportWidth();
    this.bind();
    this.start();
  }

  /* Pin the root's width to the LAYOUT viewport.

     document.documentElement.clientWidth, not innerWidth and not 100vw. On a
     page that overflows horizontally the initial containing block inflates to
     the overflow width, and anything resolving against it puts a fixed corner
     widget off the side of the screen. Measured on this theme's home page at a
     375px screen: clientWidth 375, innerWidth 526, pill ~170px past the right
     edge and invisible. clientWidth does not inflate and excludes the
     scrollbar, so it is right in both directions. */
  /* Is the open card pinned to the top of the screen right now?

     Three conditions, all live: the setting says top, the card is open, and
     we are under the theme's 750px breakpoint. Read fresh every time rather
     than cached at init — a phone rotated to landscape crosses the
     breakpoint without reloading the page. */
  MPSignupPill.prototype.isTopAnchored = function () {
    if (this.config.mobileOpen !== 'top') return false;
    if (!this.isOpen) return false;
    if (!window.matchMedia) return false;
    return window.matchMedia('(max-width: 749px)').matches;
  };

  /* The two offsets are mutually exclusive and this is the only writer.

     Corner-anchored: the card hangs off the BOTTOM edge, so an open keyboard
     hides it and --mp-pill-kb-offset lifts it by the keyboard's height. The
     >150px floor keeps a URL bar collapsing on scroll from being mistaken
     for a keyboard.

     Top-anchored: the card hangs off the TOP edge, which the keyboard never
     covers, so the lift is not just unnecessary — applying it is the bug
     Monkey reported, because iOS ALSO scrolls the visual viewport and the two
     corrections stack and throw the card off the top of the screen. So the
     lift goes to zero and we track visualViewport.offsetTop instead, which
     holds the card still relative to what the shopper can actually see. */
  MPSignupPill.prototype.syncMobileAnchor = function () {
    var vv = window.visualViewport;

    if (this.isTopAnchored()) {
      this.root.style.setProperty('--mp-pill-kb-offset', '0px');
      var top = vv ? Math.max(0, Math.round(vv.offsetTop)) : 0;
      this.root.style.setProperty('--mp-pill-vv-top', top + 'px');
      return;
    }

    this.root.style.setProperty('--mp-pill-vv-top', '0px');

    if (!this.isOpen || !vv) {
      this.root.style.setProperty('--mp-pill-kb-offset', '0px');
      return;
    }
    var delta = window.innerHeight - vv.height;
    var offset = delta > 150 ? Math.round(delta) : 0;
    this.root.style.setProperty('--mp-pill-kb-offset', offset + 'px');
  };

  MPSignupPill.prototype.pinViewportWidth = function () {
    /* No visualViewport.scale guard here, deliberately. The standing advice
       to skip while scale !== 1 applies to window.inner*, which DOES track
       pinch zoom. clientWidth is the LAYOUT viewport and is invariant under
       zoom, so there is nothing to guard against — and the guard actively
       broke the thing it was copied into. Measured: on a page that overflows
       horizontally the browser zooms out to fit, reporting scale 0.64 at a
       320px screen and 0.71 at 375px. The guard then refused every pin and
       left the CSS fallback in charge, which is the exact failure this
       method exists to prevent. It only showed up on overflowing pages,
       i.e. only where it mattered. */
    var width = document.documentElement.clientWidth;
    if (!width) return;
    this.pinnedWidth = width;
    this.root.style.setProperty('--mp-pill-vw', width + 'px');
  };

  MPSignupPill.prototype.bind = function () {
    var self = this;

    if (this.toggle) {
      this.toggle.addEventListener('click', function () {
        /* A drag ends in a click too. Swallow that one, or letting go of a
           swipe would immediately open the card the swipe was getting rid
           of. Reset on the way through so only the one click is lost. */
        if (self.suppressClick) {
          self.suppressClick = false;
          return;
        }
        if (self.isSnoozed) {
          self.unsnooze();
          return;
        }
        self.open();
      });
      if (this.config.wobbleOnHover) {
        /* pointerenter, not mouseenter: pointer events cover pen and the
           first touch, and a filter written for one input device is blind
           to the others. */
        this.toggle.addEventListener('pointerenter', function () {
          self.nudge();
        });
      }

      /* Drop the attribute once the rock finishes so the pill is genuinely
         at rest between cycles rather than holding a filled final frame.
         The final keyframe is 0deg and the base rule is rotate(0deg), so
         there is nothing to jump to. */
      this.toggle.addEventListener('animationend', function (e) {
        if (e.target !== self.toggle) return;
        self.root.removeAttribute('data-mp-rocking');
      });
    }

    /* The X snoozes — Monkey's ask, 2026-10-07. See the ways-out note at the
       top of the file.

       It falls back to dismissing when minimising is switched off, because
       snooze() is a no-op in that state and a corner button that does
       literally nothing is worse than one that does the blunt thing. That
       fallback is also what the aria-label in the section branches on, so
       the two have to be read together: change one and change the other.

       focus: true because the card is about to be display:none with the
       keyboard still inside it, and focus on a hidden element is focus thrown
       back to <body>. Hand it to the pill instead. */
    Array.prototype.slice
      .call(this.root.querySelectorAll('[data-mp-pill-close]'))
      .forEach(function (btn) {
        btn.addEventListener('click', function () {
          if (self.config.allowSnooze) {
            self.snooze({ focus: true });
          } else {
            self.dismiss({ via: 'close' });
          }
        });
      });

    if (this.smsForm) {
      this.smsForm.addEventListener('submit', function (event) {
        event.preventDefault();
        self.handleSmsSubmit();
      });
    }

    if (this.smsSkip) {
      this.smsSkip.addEventListener('click', function () {
        self.skipSms();
      });
    }

    if (this.phone) {
      this.phone.addEventListener('keydown', function (event) {
        self.onPhoneKeydown(event);
      });
      /* Also clears the SMS error, which the email field has always done on
         its own input and this one did not — the "that number did not look
         right" line used to sit there while the shopper retyped. */
      this.phone.addEventListener('input', function () {
        self.onPhoneInput();
      });
    }

    /* "Not interested in 10% off my order." The only control that writes the
       dismissal record. Step 1 only — it is not rendered on the other two. */
    if (this.optout) {
      this.optout.addEventListener('click', function () {
        self.dismiss({ via: 'optout' });
      });
    }

    this.bindDrag();

    if (this.form) {
      this.form.addEventListener('submit', function (event) {
        event.preventDefault();
        self.handleSubmit();
      });
    }

    if (this.input) {
      this.input.addEventListener('input', function () {
        self.clearError();
      });
    }

    this.onKeydown = function (event) {
      if (event.key === 'Escape' && self.isOpen) {
        self.collapse();
      }
    };
    document.addEventListener('keydown', this.onKeydown);

    /* Re-pin on WIDTH change only. A mobile browser fires resize every time
       its chrome collapses on scroll, which changes the height and nothing
       else; re-pinning on that would be pure churn. setTimeout rather than
       requestAnimationFrame because rAF is suspended in a background tab. */
    this.onResize = function () {
      if (self.resizeTimer) return;
      self.resizeTimer = window.setTimeout(function () {
        self.resizeTimer = null;
        if (document.documentElement.clientWidth !== self.pinnedWidth) {
          self.pinViewportWidth();
        }
        /* Unconditional: a rotation can cross the 750px breakpoint without
           changing clientWidth in the emulator, and the anchor has to follow
           the breakpoint, not the pin. */
        self.syncMobileAnchor();
      }, 150);
    };
    window.addEventListener('resize', this.onResize);
    /* The theme editor resizes the preview frame without a window resize
       event in some flows; a section reload re-runs init, which re-pins. */

    /* iOS: an open keyboard shrinks the VISUAL viewport but leaves a
       position:fixed bottom edge sitting underneath it, so the card — and
       the field being typed into — disappear behind the keyboard. Lift the
       root by the difference while that is true. Chrome on Android resizes
       the layout viewport instead and reports a delta of ~0, so this is a
       no-op there rather than a double correction. */
    if (window.visualViewport) {
      this.onViewport = function () {
        self.syncMobileAnchor();
      };
      window.visualViewport.addEventListener('resize', this.onViewport);
      /* scroll, not just resize. On iOS the keyboard does not only shrink the
         visual viewport, it also SCROLLS it — offsetTop becomes non-zero and
         a position:fixed top edge drifts upward out of sight with it. resize
         fires once when the keyboard animates in; offsetTop keeps moving
         after that, and only the scroll event reports it. */
      window.visualViewport.addEventListener('scroll', this.onViewport);
    }
  };

  /* ---------------------------------------------------------------------
     Swipe to minimise

     Pointer events, and the move/up listeners live on WINDOW rather than on
     the pill: a fast flick leaves the button under the finger well before
     the gesture ends, and listeners bound to the button stop hearing about
     it at exactly the moment the gesture gets interesting.

     Horizontal only. The axis test below abandons the drag the moment the
     movement looks vertical, which — with touch-action: pan-y in the CSS —
     means a shopper who starts a page scroll with their thumb on the pill
     scrolls the page, and nothing else happens.
     --------------------------------------------------------------------- */
  MPSignupPill.prototype.maxShift = function () {
    if (!this.toggle) return 0;
    var cs = window.getComputedStyle(this.root);
    var peek = parseFloat(cs.getPropertyValue('--mp-pill-peek')) || 32;
    var offset = parseFloat(cs.getPropertyValue('--mp-pill-offset-x')) || 0;
    /* offsetWidth, measured live: the pill's width is the label's width and
       the label is a merchant setting. Matches the CSS, which translates by
       100% - peek + offset_x. */
    return Math.max(0, this.toggle.offsetWidth - peek + offset);
  };

  MPSignupPill.prototype.bindDrag = function () {
    var self = this;
    if (!this.toggle) return;
    if (!this.config.allowSnooze || !this.config.swipe) return;
    if (!window.PointerEvent) return;

    this.onPointerDown = function (event) {
      if (self.isOpen) return;
      /* Primary button only. A right-click that started a drag would leave
         the pill stranded mid-screen with no click to finish it. */
      if (typeof event.button === 'number' && event.button !== 0) return;
      self.drag = {
        id: event.pointerId,
        x: event.clientX,
        y: event.clientY,
        max: self.maxShift(),
        from: self.isSnoozed ? self.maxShift() : 0,
        shift: self.isSnoozed ? self.maxShift() : 0,
        active: false
      };
    };

    this.onPointerMove = function (event) {
      var d = self.drag;
      if (!d || event.pointerId !== d.id) return;
      var dx = event.clientX - d.x;
      var dy = event.clientY - d.y;

      if (!d.active) {
        if (Math.abs(dx) < 6 && Math.abs(dy) < 6) return;
        if (Math.abs(dy) > Math.abs(dx)) {
          /* Vertical: this is a scroll, not a swipe. Let go of it entirely
             rather than competing with the page. */
          self.drag = null;
          return;
        }
        d.active = true;
        self.stopWobble();
        self.root.dataset.mpDragging = 'true';
        if (self.toggle) self.toggle.style.transform = '';
      }

      var shift = d.from + dx * self.awaySign;
      if (shift < 0) shift = 0;
      if (shift > d.max) shift = d.max;
      d.shift = shift;
      self.toggle.style.transform = 'translateX(' + shift * self.awaySign + 'px)';
      /* Swap the label for the chevron once the release would minimise, so
         the pill shows you the outcome before you commit to it. */
      self.root.dataset.mpPeeking = shift > d.max * 0.4 ? 'true' : 'false';
    };

    this.onPointerUp = function (event) {
      var d = self.drag;
      if (!d || event.pointerId !== d.id) return;
      self.drag = null;
      self.root.removeAttribute('data-mp-dragging');
      self.root.removeAttribute('data-mp-peeking');
      if (!d.active) return; /* a tap — the click handler owns it */

      /* Hand the transform back to the stylesheet before changing state, so
         the snap animates from wherever the finger left it. An inline
         transform would beat the CSS one and freeze the pill there. */
      if (self.toggle) self.toggle.style.transform = '';
      self.holdClick();

      if (d.shift > d.max * 0.4) {
        self.snooze();
      } else if (self.isSnoozed) {
        self.unsnooze();
      } else {
        self.root.dataset.mpState = 'collapsed';
        self.startWobble();
      }
    };

    this.toggle.addEventListener('pointerdown', this.onPointerDown);
    window.addEventListener('pointermove', this.onPointerMove, { passive: true });
    window.addEventListener('pointerup', this.onPointerUp);
    window.addEventListener('pointercancel', this.onPointerUp);
  };

  /* Arm the click swallow, with a fuse. A drag that ends outside the button
     — or one the browser cancels — produces no click at all, and a flag left
     standing would eat the shopper's NEXT genuine tap instead. */
  MPSignupPill.prototype.holdClick = function () {
    var self = this;
    this.suppressClick = true;
    if (this.suppressClickTimer) window.clearTimeout(this.suppressClickTimer);
    this.suppressClickTimer = window.setTimeout(function () {
      self.suppressClick = false;
      self.suppressClickTimer = null;
    }, 400);
  };

  /* ---------------------------------------------------------------------
     Appearance
     --------------------------------------------------------------------- */
  MPSignupPill.prototype.start = function () {
    var self = this;
    var designMode = window.Shopify && window.Shopify.designMode;

    if (designMode) {
      /* In the theme editor, ignore every suppression rule — a merchant
         styling the success state must be able to see the success state,
         and a dismissal from an earlier session must not make the section
         look broken. The preview state is a section setting. */
      this.reveal();
      if (this.config.designPreview === 'expanded') {
        this.open({ silent: true });
      } else if (this.config.designPreview === 'success') {
        this.open({ silent: true });
        this.showSuccess();
      } else if (this.config.designPreview === 'snoozed' && this.config.allowSnooze) {
        this.snooze();
      }
      return;
    }

    if (!this.config.remember) {
      /* Setting is off: forget the DISMISSAL, so that switching it back on
         later starts from a clean slate instead of re-applying a close from
         weeks ago.

         It used to clear the minimise and the signup record here too. That
         is what made minimise non-persistent on this store and what
         re-prompted people who had already subscribed, both because the
         switch is off in the saved settings. Those two records are not
         dismissals and no longer answer to this setting. */
      removeStore(STORE_DISMISSED);
    }

    if (this.isSuppressed()) {
      return;
    }

    if (this.config.appearOn === 'scroll') {
      this.watchScroll();
    } else {
      window.setTimeout(function () {
        self.reveal();
      }, this.config.appearDelay * 1000);
    }
  };

  MPSignupPill.prototype.isSuppressed = function () {
    /* Checked FIRST, and deliberately outside the remember flag: someone who
       has already given us their email must never be asked again, whatever
       the dismissal setting says. The setting is for previewing a close
       repeatedly, and nobody needs to preview a signup repeatedly. */
    if (readStore(STORE_SUBSCRIBED) === '1') return true;

    /* "Remember that it was closed" off: a close is not remembered, so
       every page load shows the pill again. */
    if (!this.config.remember) return false;

    var dismissedAt = parseInt(readStore(STORE_DISMISSED) || '0', 10);
    if (!dismissedAt) return false;

    /* 0 days means "dismissed for this browser, permanently" — the setting
       reads that way in the editor and has to behave that way here, so an
       explicit 0 must not fall through to the elapsed-time comparison. */
    if (this.config.dismissDays === 0) return true;

    var elapsed = Date.now() - dismissedAt;
    return elapsed < this.config.dismissDays * 86400000;
  };

  MPSignupPill.prototype.watchScroll = function () {
    var self = this;
    var check = function () {
      var doc = document.documentElement;
      var scrollable = doc.scrollHeight - doc.clientHeight;
      /* A page shorter than the viewport has nothing to scroll, so a
         percentage threshold can never be met and the pill would never
         appear at all. Reveal immediately instead of never. */
      if (scrollable <= 0) {
        self.reveal();
        self.stopScrollWatch();
        return;
      }
      var percent = (window.scrollY / scrollable) * 100;
      if (percent >= self.config.scrollThreshold) {
        self.reveal();
        self.stopScrollWatch();
      }
    };

    this.onScroll = function () {
      /* setTimeout, not requestAnimationFrame: rAF is suspended in a
         backgrounded or hidden tab, and a throttled rAF would leave the
         pill stuck hidden after the shopper returns. */
      if (self.scrollTimer) return;
      self.scrollTimer = window.setTimeout(function () {
        self.scrollTimer = null;
        check();
      }, 120);
    };

    window.addEventListener('scroll', this.onScroll, { passive: true });
    check();
  };

  MPSignupPill.prototype.stopScrollWatch = function () {
    if (this.onScroll) {
      window.removeEventListener('scroll', this.onScroll);
      this.onScroll = null;
    }
    if (this.scrollTimer) {
      window.clearTimeout(this.scrollTimer);
      this.scrollTimer = null;
    }
  };

  MPSignupPill.prototype.reveal = function () {
    if (this.root.dataset.mpState !== 'hidden') return;

    /* A live snooze is not a suppression: the pill still appears, it just
       appears already parked at the edge. isSuppressed() deliberately knows
       nothing about it. */
    if (this.isSnoozeActive()) {
      this.isSnoozed = true;
      this.enterSnoozed();
      this.startSnoozeNudge();
      return;
    }

    this.root.dataset.mpState = 'collapsed';
    this.startWobble();
    this.trackViewed();
    this.watchAutoExpand();
  };

  /* Enter the minimised state as an inward slide, not an outward sweep.
     Monkey, 2026-10-08: on every page change the minimised tab "forms from
     the left" and travels across the corner, which is distracting. It should
     arrive from off the edge instead — the same shape as one of its nudges.

     The sweep was never an entrance. It is the toggle's transform transition
     running from its base `rotate(0deg)` to the snoozed translate, which
     happens to start at the pill's full on-screen position. The fix gives it
     a real "from" state: parked fully past the edge, committed with the
     transition off, then released so the snoozed rule carries it back in by
     one peek.

     THE FLUSH IS THE WHOLE TRICK, and it is the same one nudge() documents:
     setting and removing an attribute in one task is a no-op, because no
     style recalculation happens in between. Reading a layout property forces
     that recalculation, so the parked position is genuinely rendered and the
     removal has something to animate from.

     Deliberately NOT rAF, unlike the card's entrance. rAF is suspended in a
     background tab, and a tab restored after being opened in the background
     would find the attribute still set and the tab parked off-screen — the
     widget would simply be missing. A same-task flush cannot strand it.

     Only the reveal path calls this. A shopper who presses the X should still
     see the pill travel out to the edge: that animation is the feedback that
     their tap did something, and it is the one place the sweep is correct. */
  MPSignupPill.prototype.enterSnoozed = function () {
    if (!this.toggle || this.reduceMotion) {
      this.root.dataset.mpState = 'snoozed';
      return;
    }

    this.root.dataset.mpSnoozeEnter = 'true';
    this.root.dataset.mpState = 'snoozed';
    void this.toggle.offsetWidth;
    this.root.removeAttribute('data-mp-snooze-enter');
  };

  /* ---------------------------------------------------------------------
     Auto-expand (v10, Monkey 2026-10-07)

     The pill opens itself once, after the shopper has scrolled past a
     threshold, and then never again for the rest of the scope. It is a
     nudge, not a popup: it waits for a signal of interest instead of
     interrupting the landing.

     Four things it deliberately does NOT do:

     - It does not fire on a page too short to scroll. watchScroll() reveals
       immediately in that case because a pill nobody can summon is worse
       than an early one; an auto-OPEN on load is the intrusive modal this
       whole section exists to replace, so the opposite answer is right here.
     - It does not focus the field. open({silent:true}). Focusing would pop
       the keyboard on a phone for someone who never asked for a form, which
       on a page they are mid-scroll through is worse than the old popup.
     - It does not fire over the shopper. Anything other than a plain
       collapsed pill — open, minimised, subscribed, gone — and it stands down
       permanently for the scope.
     - It does not get a second chance. The record is written BEFORE open()
       so that a throw inside open() cannot leave the watcher armed and
       firing on every scroll tick.
     --------------------------------------------------------------------- */
  MPSignupPill.prototype.autoExpandUsed = function () {
    if (window.Shopify && window.Shopify.designMode) return true;
    var raw = readScoped(STORE_AUTOEXPANDED, this.config.autoExpandScope);
    if (!raw) return false;
    /* Session scope: the record existing IS the answer, same as the snooze. */
    if (this.config.autoExpandScope !== 'hours') return true;
    var at = parseInt(raw, 10);
    if (!at) return false;
    if (Date.now() - at < this.config.autoExpandHours * 3600000) return true;
    clearScoped(STORE_AUTOEXPANDED);
    return false;
  };

  MPSignupPill.prototype.markAutoExpandUsed = function () {
    this.stopAutoExpandWatch();
    if (window.Shopify && window.Shopify.designMode) return;
    writeScoped(STORE_AUTOEXPANDED, this.config.autoExpandScope, String(Date.now()));
  };

  MPSignupPill.prototype.watchAutoExpand = function () {
    var self = this;
    if (!this.config.autoExpand) return;
    if (this.onAutoScroll) return;
    if (this.autoExpandUsed()) return;
    if (readStore(STORE_SUBSCRIBED) === '1') return;

    var check = function () {
      /* Re-checked every tick, not just at arm time: the shopper can open,
         minimise or opt out at any point between the two. */
      if (self.root.dataset.mpState !== 'collapsed' || self.isOpen || self.isSnoozed) {
        self.stopAutoExpandWatch();
        return;
      }
      var doc = document.documentElement;
      var scrollable = doc.scrollHeight - doc.clientHeight;
      if (scrollable <= 0) return;
      var percent = (window.scrollY / scrollable) * 100;
      if (percent < self.config.autoExpandThreshold) return;

      self.markAutoExpandUsed();
      self.root.dataset.mpOpenedBy = 'auto';
      self.open({ silent: true });
    };

    this.onAutoScroll = function () {
      /* setTimeout rather than rAF, for the reason given on watchScroll(). */
      if (self.autoScrollTimer) return;
      self.autoScrollTimer = window.setTimeout(function () {
        self.autoScrollTimer = null;
        check();
      }, 120);
    };

    window.addEventListener('scroll', this.onAutoScroll, { passive: true });
    /* No immediate check(). Landing deep-linked at an anchor halfway down a
       page is not "the shopper scrolled"; the first real scroll event is. */
  };

  MPSignupPill.prototype.stopAutoExpandWatch = function () {
    if (this.onAutoScroll) {
      window.removeEventListener('scroll', this.onAutoScroll);
      this.onAutoScroll = null;
    }
    if (this.autoScrollTimer) {
      window.clearTimeout(this.autoScrollTimer);
      this.autoScrollTimer = null;
    }
  };

  /* "Step 1 became visible to the shopper", once per session.

     Fired when the PILL appears, not when the card is opened, and that is a
     judgement call worth stating because it decides what the submit rate
     means. The Klaviyo form it replaces opens itself, so a view there is
     "the offer was put in front of them" — and the pill appearing is the
     same moment. Counting the card instead would count a deliberate click
     as a view, which would roughly halve the denominator and make the new
     form look dramatically better than the old one for no real reason.

     Once per session, not once per page: the pill reveals on every page
     load, and counting each one would inflate views without inflating
     submits. Same rule the Klaviyo form follows today. */
  MPSignupPill.prototype.trackViewed = function () {
    if (window.Shopify && window.Shopify.designMode) return;
    if (readSession(SESSION_VIEWED) === '1') return;
    writeSession(SESSION_VIEWED, '1');
    trackKlaviyo(this.config.trackEvents, 'Signup Form Viewed', {
      form: this.config.signupSource,
      step: 1
    });
  };

  MPSignupPill.prototype.isSnoozeActive = function () {
    if (!this.config.allowSnooze) return false;
    if (window.Shopify && window.Shopify.designMode) return false;

    var raw = readSnooze(this.config.snoozeScope);
    if (!raw) return false;

    /* Session scope: the record existing IS the answer. sessionStorage is
       per-tab and the browser drops it when the tab closes, so there is no
       window to compare against and nothing to expire — that is the whole
       point of the scope. */
    if (this.config.snoozeScope !== 'hours') return true;

    var at = parseInt(raw, 10);
    if (!at) return false;
    if (Date.now() - at < this.config.snoozeHours * 3600000) return true;

    /* Expired. Clear it rather than re-reading a dead timestamp on every
       page load for the rest of the browser's life. */
    clearSnooze();
    return false;
  };

  /* ---------------------------------------------------------------------
     The wobble. This is the interaction from the reference: the pill rocks
     between +angle and -angle on a timer, and again on hover. It is NOT
     scroll-driven — the reference has no scroll behaviour at all.
     --------------------------------------------------------------------- */
  MPSignupPill.prototype.startWobble = function () {
    var self = this;
    if (!this.config.wobble || this.wobbleTimer) return;
    /* Reduced motion means no timer at all. A fade stand-in was tried for
       one round on 2026-10-08 and withdrawn — see mp-signup-pill.css. */
    if (this.reduceMotion) return;

    this.wobbleDelayTimer = window.setTimeout(function () {
      /* Rock once immediately, THEN start the interval. setInterval alone
         waits a full period before the first call, so the schema's "starts
         2 seconds after the pill appears" was off by the interval — at the
         default that is a 5 second wait for the first movement, which is
         most of the reason it read as not working at all. */
      self.nudge();
      self.wobbleTimer = window.setInterval(function () {
        if (self.isOpen) return;
        self.nudge();
      }, self.config.wobbleInterval);
    }, 2000);
  };

  MPSignupPill.prototype.stopWobble = function () {
    if (this.wobbleDelayTimer) {
      window.clearTimeout(this.wobbleDelayTimer);
      this.wobbleDelayTimer = null;
    }
    if (this.wobbleTimer) {
      window.clearInterval(this.wobbleTimer);
      this.wobbleTimer = null;
    }
    this.root.removeAttribute('data-mp-rocking');
  };

  /* One rock: square, over to one side, back past square to the other, and
     settle at square again.

     What this replaced, and why: the first version wrote an inline
     `transform: rotate(6deg)`, then `-6deg`, then `6deg` on the interval.
     That is a lean that flips, not a rock. The pill was never at rest — it
     sat 6 degrees off square for the whole gap between flips and moved for
     300ms out of every 3000. Measured at 1280 and 390 on 2026-09-16: the
     behaviour was identical on both, so "works on mobile, not on desktop"
     was really "too subtle to read anywhere, and marginally easier to
     notice against a narrow screen".

     The motion itself now lives in a keyframe animation in the section's
     style block. This function only re-triggers it. That also retires the
     inline transform, which used to beat the stylesheet's translateX and
     had to be hand-cleared on the way into the minimised state. */
  MPSignupPill.prototype.nudge = function () {
    if (!this.toggle) return;
    if (!this.config.wobble) return;
    /* A rotation is exactly what the reduced-motion preference is asking us
       not to do, and nothing stands in for it. */
    if (this.reduceMotion) return;
    /* A rock while open, minimised or mid-drag would either be invisible or
       fight a transform that means something. */
    if (this.isOpen || this.isSnoozed) return;
    if (this.root.dataset.mpDragging === 'true') return;

    /* Restart, not start. Removing and re-adding the attribute in the same
       task is a no-op — no style recalculation happens in between, so the
       running animation is never interrupted and a hover during a rock does
       nothing at all. Reading a layout property forces the flush that makes
       the restart real. */
    this.root.removeAttribute('data-mp-rocking');
    void this.toggle.offsetWidth;
    this.root.dataset.mpRocking = 'true';
  };


  /* ---------------------------------------------------------------------
     The minimised nudge. Monkey's ask, 2026-10-07: once the pill is parked
     at the edge it should move every so often so it does not disappear
     into the page.

     IT IS A POKE, NOT A ROCK, and that is deliberate. The collapsed pill
     rocks, because a free-floating pill can rotate about its own centre and
     read as a wobble. The minimised tab is clipped by the viewport edge with
     only --mp-pill-peek of it on screen: rotating that lifts one corner off
     the edge and reads as a rendering fault, not as movement. So the tab
     slides a few pixels back into the page and settles again — the same
     gesture as the swipe that brings it back, previewed.

     No keyframes. The toggle already carries `transition: transform`, and
     the minimised position is a per-corner translateX in the stylesheet, so
     one extra attribute that shifts that translate gets the whole animation
     for free and composes with the corner rules instead of fighting them.
     A keyframe would have had to restate the per-corner translate, including
     inside the mobile media query where the offset changes.
     --------------------------------------------------------------------- */
  MPSignupPill.prototype.startSnoozeNudge = function () {
    var self = this;
    if (!this.config.snoozeWobble) return;
    /* Same deal as startWobble: under reduced motion there is no timer. */
    if (this.reduceMotion) return;
    if (this.snoozeNudgeTimer) return;

    this.snoozeNudgeTimer = window.setInterval(function () {
      self.snoozeNudge();
    }, this.config.snoozeWobbleInterval);
  };

  MPSignupPill.prototype.stopSnoozeNudge = function () {
    if (this.snoozeNudgeTimer) {
      window.clearInterval(this.snoozeNudgeTimer);
      this.snoozeNudgeTimer = null;
    }
    if (this.snoozeNudgeBack) {
      window.clearTimeout(this.snoozeNudgeBack);
      this.snoozeNudgeBack = null;
    }
    this.root.removeAttribute('data-mp-snooze-nudge');
  };

  MPSignupPill.prototype.snoozeNudge = function () {
    var self = this;
    if (!this.config.snoozeWobble) return;
    /* The slide is movement, so it does not run under reduced motion, and
       nothing stands in for it. */
    if (this.reduceMotion) return;
    /* Only from a settled minimised state. Mid-drag the transform is being
       written frame by frame from JS and this would fight it; open or
       collapsed there is no edge position to poke out of. */
    if (this.root.dataset.mpState !== 'snoozed') return;
    if (this.root.dataset.mpDragging === 'true') return;
    if (this.root.dataset.mpPeeking === 'true') return;
    /* A nudge nobody can see is wasted, and the interval keeps firing in a
       background tab — throttled, but it still fires. Skip rather than
       burn a transition the shopper will never watch. */
    if (document.hidden) return;
    /* Already mid-nudge: let it finish rather than restarting it. */
    if (this.snoozeNudgeBack) return;

    this.root.dataset.mpSnoozeNudge = 'true';
    /* Out and back. The return leg is the same transition in reverse, so the
       hold only has to outlast the outbound one — anim_duration, in ms, read
       off the computed custom property rather than assumed, because it is a
       merchant setting. */
    var out = this.config.animDuration;
    this.snoozeNudgeBack = window.setTimeout(function () {
      self.snoozeNudgeBack = null;
      self.root.removeAttribute('data-mp-snooze-nudge');
    }, out + 120);
  };

  /* ---------------------------------------------------------------------
     Open / close
     --------------------------------------------------------------------- */
  MPSignupPill.prototype.open = function (options) {
    var self = this;
    if (this.isOpen) return;
    this.isOpen = true;
    this.stopWobble();
    this.stopSnoozeNudge();
    /* Any open spends the budget, including a deliberate tap on the pill.
       Opening it yourself and then having it open itself again twenty
       seconds later is the nag this feature is one scroll away from being. */
    this.markAutoExpandUsed();

    this.root.dataset.mpState = 'open';
    this.syncMobileAnchor();
    if (this.toggle) {
      this.toggle.setAttribute('aria-expanded', 'true');
      this.toggle.hidden = true;
    }
    if (this.card) {
      this.card.hidden = false;
      this.card.dataset.mpEnter = 'true';
      /* Two frames, not one. A single rAF can land in the same style
         recalculation as the unhide, which collapses the transition to
         nothing — the element never has a rendered "from" state. */
      window.requestAnimationFrame(function () {
        window.requestAnimationFrame(function () {
          if (self.card) self.card.dataset.mpEnter = 'false';
        });
      });
    }

    /* Focus the field belonging to the step that is actually on screen.
       ESC collapses from any step without resetting it, so re-opening can
       land on step 2 — and focusing the email input then would be focusing
       an element inside a hidden container, which browsers answer by
       throwing focus back to <body>. */
    if (!(options && options.silent)) {
      var field = this.step === 2 ? this.phone : this.input;
      if (field && !this.isSubscribedState()) field.focus();
    }
  };

  /* ESC: back out to the pill, nothing remembered. An accidental keypress
     should not cost a shopper the offer for a week. */
  MPSignupPill.prototype.collapse = function () {
    if (!this.isOpen) return;

    if (this.isSubscribedState()) {
      /* Nothing left to ask for, so this does not collapse back to a pill
         that reopens onto a thank-you. It snoozes, which leaves the edge tab
         and keeps the discount code one tap away — the same answer the X
         gives on this step since 2026-10-07. Dismissing here would have ESC
         throw the code away while the X keeps it, which is a contradiction
         the shopper has no way to predict. */
      if (this.config.allowSnooze) {
        this.snooze({ focus: true });
      } else {
        this.dismiss({ via: 'esc' });
      }
      return;
    }

    this.isOpen = false;
    this.root.dataset.mpState = 'collapsed';
    if (this.card) this.card.hidden = true;
    if (this.toggle) {
      this.toggle.hidden = false;
      this.toggle.setAttribute('aria-expanded', 'false');
      this.toggle.focus();
    }
    this.root.style.setProperty('--mp-pill-kb-offset', '0px');
    this.root.style.setProperty('--mp-pill-vv-top', '0px');
    this.startWobble();
  };

  /* Minimise: out of the way, not gone. Reached from the button in the
     card, from a swipe on the pill, and from the editor preview setting. */
  MPSignupPill.prototype.snooze = function (options) {
    if (!this.config.allowSnooze) return;

    this.stopWobble();
    this.isOpen = false;
    this.isSnoozed = true;

    if (this.card) this.card.hidden = true;
    if (this.toggle) {
      this.toggle.hidden = false;
      this.toggle.setAttribute('aria-expanded', 'false');
      /* Clear any inline transform before the stylesheet's translateX takes
         over. The rock no longer writes one — it is a keyframe animation
         now, gated to the collapsed state — but the drag does, and a browser
         still holding a cached copy of the previous JS would have left an
         inline rotate here that beat the translate and parked the pill in
         place, rotated, with its label hidden. */
      this.toggle.style.transform = '';
    }

    this.root.style.setProperty('--mp-pill-kb-offset', '0px');
    this.root.style.setProperty('--mp-pill-vv-top', '0px');
    this.root.removeAttribute('data-mp-peeking');
    this.root.dataset.mpState = 'snoozed';
    /* A minimise is a "no, not now". Never open over it. */
    this.markAutoExpandUsed();

    /* Not gated on the remember setting — see the storage note at the top.
       Still gated on design mode: a merchant minimising it in the editor to
       see what it looks like must not have that state follow them onto the
       storefront. */
    if (!(window.Shopify && window.Shopify.designMode)) {
      writeSnooze(this.config.snoozeScope, String(Date.now()));
    }

    this.startSnoozeNudge();

    if (options && options.focus && this.toggle) this.toggle.focus();
  };

  MPSignupPill.prototype.unsnooze = function () {
    this.isSnoozed = false;
    this.stopSnoozeNudge();
    clearSnooze();
    this.root.removeAttribute('data-mp-peeking');
    this.root.dataset.mpState = 'collapsed';
    if (this.toggle) this.toggle.style.transform = '';
    this.startWobble();
  };

  /* The opt-out link, the success auto-close, and the X when minimising is
     switched off: gone, and remembered for the configured window.

     options.via names the control for the Klaviyo event, because after
     2026-10-07 the three are no longer the same answer and a Closed count
     that cannot tell "not interested" from a timer is not worth reading. */
  MPSignupPill.prototype.dismiss = function (options) {
    /* "Dismissed without completing the step they were on." Step 3 is the
       thank-you — closing that is finishing, not abandoning, so it does not
       count. The auto-close after a success routes through here too and must
       not file a Closed either. */
    if (this.step < 3) {
      trackKlaviyo(this.config.trackEvents, 'Signup Form Closed', {
        form: this.config.signupSource,
        step: this.step,
        via: (options && options.via) || 'optout'
      });
    }

    this.stopWobble();
    this.stopSnoozeNudge();
    this.stopScrollWatch();
    this.stopAutoExpandWatch();
    this.isOpen = false;
    this.isSnoozed = false;
    this.root.dataset.mpState = 'hidden';
    this.root.style.setProperty('--mp-pill-kb-offset', '0px');
    this.root.removeAttribute('data-mp-peeking');
    if (this.toggle) this.toggle.setAttribute('aria-expanded', 'false');
    if (!(window.Shopify && window.Shopify.designMode)) {
      if (this.config.remember) writeStore(STORE_DISMISSED, String(Date.now()));
      /* An opt-out beats an open snooze, and it clears it whether or not the
         dismissal itself is remembered — otherwise with remembering off the
         opt-out would hide the pill for this page and the edge tab would be
         back on the next one, which reads as the widget ignoring the answer
         the shopper actually typed out. */
      clearSnooze();
    }
  };

  MPSignupPill.prototype.isSubscribedState = function () {
    return !!(this.successState && !this.successState.hidden);
  };

  /* ---------------------------------------------------------------------
     Submit
     --------------------------------------------------------------------- */
  MPSignupPill.prototype.handleSubmit = function () {
    var self = this;
    if (this.isSubmitting) return;

    var email = (this.input && this.input.value ? this.input.value : '').trim();

    if (!looksLikeEmail(email)) {
      this.showError('invalid');
      if (this.input) this.input.focus();
      return;
    }

    if (!this.config.companyId || !this.config.listId) {
      /* Misconfiguration, not a shopper error. Say so in the console with
         enough detail to fix it, and show the generic message rather than
         implying the address was bad. */
      window.console.error(
        '[mp-signup-pill] missing Klaviyo config — companyId:',
        this.config.companyId || '(none)',
        'listId:',
        this.config.listId || '(none)'
      );
      this.showError('generic');
      return;
    }

    this.setSubmitting(true);
    this.clearError();

    this.subscribeToKlaviyo(email)
      .then(function () {
        self.succeed(email, 'klaviyo');

        /* Background, unawaited, and explicitly allowed to fail. See the
           header comment for why this must not gate the success state. The
           swallow is load-bearing now that this returns a rejecting promise:
           without it a captcha challenge here would surface as an unhandled
           rejection on a signup that already succeeded. */
        if (self.config.createCustomer) {
          self.createShopifyCustomer(email).catch(function () {});
        }
      })
      .catch(function (error) {
        /* Klaviyo ANSWERED and said no — a bad address, a dead list, an
           outage. The shopper sees the error and the fallback stays out of
           it: re-posting a payload the server has already rejected just gets
           it rejected again somewhere else. */
        /* The second condition is the merchant's own switch. The fallback
           creates a Shopify customer and nothing else, so "don't create
           Shopify customers" has to mean that here too — otherwise turning
           the setting off would quietly fail to turn it off. */
        if (!error || !error.mpUnreachable || !self.config.createCustomer) {
          self.setSubmitting(false);
          window.console.error('[mp-signup-pill] subscribe failed', error);
          self.showError('generic');
          return;
        }

        /* The request never left the device.

           Measured 2026-09-28: Vivaldi and the DuckDuckGo browser ship
           DuckDuckGo's Tracker Radar list, in which klaviyo.com carries
           "default": "block" with zero exception rules — so every request to
           every klaviyo.com subdomain dies at the network layer before it is
           sent. Klaviyo's own popup does not survive this either; it is
           delivered BY the blocked script, so it never renders at all and the
           shopper sees nothing and reports nothing. Ours is first-party
           markup, so it is still on screen and can still do something useful.

           What it does is post the same address to the store's own domain,
           which no blocklist can touch because it is the domain the shopper
           chose to visit. */
        window.console.warn(
          '[mp-signup-pill] Klaviyo unreachable, falling back to /contact —',
          error.message
        );
        self
          .createShopifyCustomer(email, true)
          .then(function () {
            self.succeed(email, 'shopify-fallback');
          })
          .catch(function (fallbackError) {
            /* Both doors shut. The generic message is deliberate: this file
               hardcodes no shopper-facing copy, every string is a schema
               setting, and inventing one here would be the only exception in
               the component. A blocker-specific message needs a setting. */
            self.setSubmitting(false);
            window.console.error('[mp-signup-pill] fallback failed too', fallbackError);
            self.showError('generic');
          });
      });
  };

  /* The single place the success state is raised, so the fallback path
     cannot drift away from the Klaviyo path as either changes. `via` is for
     the console and the event only — the shopper sees exactly the same card
     either way, because either way we have their address. */
  MPSignupPill.prototype.succeed = function (email, via) {
    this.setSubmitting(false);
    /* Always, regardless of the dismissal setting. See isSuppressed(). */
    writeStore(STORE_SUBSCRIBED, '1');
    /* Held for step 2: Klaviyo matches the SMS profile on the email address,
       so the second call needs it and the shopper must not have to retype
       anything they have already given us. */
    this.email = email;

    trackKlaviyo(this.config.trackEvents, 'Signup Form Submitted', {
      form: this.config.signupSource,
      step: 1
    });

    if (typeof window.fbq === 'function') {
      window.fbq('track', 'Lead');
    }
    document.dispatchEvent(
      new CustomEvent('mp:signup-pill:subscribed', { detail: { email: email, via: via } })
    );

    /* The email is already on the list by the time this runs, which is what
       makes a second screen safe to show at all. Everyone who abandons here
       is still subscribed. */
    if (this.config.smsStep) {
      this.showSmsStep();
      return;
    }
    this.showSuccess();
  };

  /* ---------------------------------------------------------------------
     Step 2 — the phone number
     --------------------------------------------------------------------- */
  MPSignupPill.prototype.showSmsStep = function () {
    this.step = 2;
    if (this.formState) this.formState.hidden = true;
    if (this.smsState) this.smsState.hidden = false;

    trackKlaviyo(this.config.trackEvents, 'Signup Form Viewed', {
      form: this.config.signupSource,
      step: 2
    });

    /* Focus the heading, not the phone field. Opening a numeric keyboard
       unasked on a screen whose honest answer is often "no thanks" pushes
       the skip button under the keyboard on a short phone. */
    var heading = this.root.querySelector('[data-mp-pill-sms-heading]');
    if (heading && typeof heading.focus === 'function') heading.focus();
  };

  MPSignupPill.prototype.handleSmsSubmit = function () {
    var self = this;
    if (this.isSubmitting) return;

    var phone = normalisePhone(this.phone && this.phone.value);

    if (!phone) {
      this.showSmsError('phoneInvalid');
      if (this.phone) this.phone.focus();
      return;
    }

    if (!this.config.companyId || !this.config.smsListId) {
      window.console.error(
        '[mp-signup-pill] missing Klaviyo SMS config — companyId:',
        this.config.companyId || '(none)',
        'smsListId:',
        this.config.smsListId || '(none)'
      );
      this.showSmsError('generic');
      return;
    }

    this.setSmsSubmitting(true);
    this.clearSmsError();

    this.subscribeSmsToKlaviyo(this.email, phone)
      .then(function () {
        self.setSmsSubmitting(false);
        trackKlaviyo(self.config.trackEvents, 'Signup Form Submitted', {
          form: self.config.signupSource,
          step: 2
        });
        document.dispatchEvent(
          new CustomEvent('mp:signup-pill:sms-subscribed', {
            detail: { email: self.email, phone: phone }
          })
        );
        self.showSuccess();
      })
      .catch(function (error) {
        /* The email subscription from step 1 stands, whatever happened here.
           It was a separate call to a separate list and nothing on this
           screen can undo it — which is exactly why it is a separate call.
           So the shopper stays on step 2 with an error and the skip button
           still in front of them, rather than being told the whole signup
           failed. */
        self.setSmsSubmitting(false);
        window.console.error('[mp-signup-pill] SMS subscribe failed', error);
        self.showSmsError(error && error.mpInvalidNumber ? 'phoneInvalid' : 'generic');
      });
  };

  /* "No thanks, go to code". Not a dismissal and not a failure: they have
     the email subscription and they get the reward. No SMS consent object
     is sent, ever — a phone number nobody typed cannot be consented to. */
  MPSignupPill.prototype.skipSms = function () {
    /* No event of its own. The brief specifies three metrics and a fourth
       would be one more thing to read for no new information: skips are
       already (Viewed step 2) - (Submitted step 2) - (Closed step 2). */
    this.showSuccess();
  };

  /* A SEPARATE call to a SEPARATE list, fired later than the email one.
     Both of those are deliberate: a 400 from a bad number must not be able
     to block, undo or cast doubt on a subscription that already returned
     202, and SMS consent written against the email list would be a consent
     record for a channel nobody agreed to. */
  MPSignupPill.prototype.subscribeSmsToKlaviyo = function (email, phone) {
    var payload = {
      data: {
        type: 'subscription',
        attributes: {
          custom_source: this.config.customSource,
          profile: {
            data: {
              type: 'profile',
              attributes: {
                email: email,
                phone_number: phone,
                properties: { 'Signup Source': this.config.signupSource },
                subscriptions: { sms: { marketing: { consent: 'SUBSCRIBED' } } }
              }
            }
          }
        },
        relationships: {
          list: { data: { type: 'list', id: this.config.smsListId } }
        }
      }
    };

    return window
      .fetch(KLAVIYO_ENDPOINT + '?company_id=' + encodeURIComponent(this.config.companyId), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/vnd.api+json',
          revision: KLAVIYO_REVISION
        },
        body: JSON.stringify(payload)
      })
      .then(function (response) {
        if (response.ok) return true;
        /* 400 here is overwhelmingly "that is not a real mobile number" —
           Klaviyo validates the line type, which no client-side regex can.
           Say so, rather than showing a generic failure for something the
           shopper can fix by retyping. */
        var error = new Error('Klaviyo responded ' + response.status);
        if (response.status === 400) error.mpInvalidNumber = true;
        throw error;
      });
  };

  MPSignupPill.prototype.subscribeToKlaviyo = function (email) {
    var payload = {
      data: {
        type: 'subscription',
        attributes: {
          custom_source: this.config.customSource,
          profile: {
            data: {
              type: 'profile',
              attributes: {
                email: email,
                properties: { 'Signup Source': this.config.signupSource },
                subscriptions: { email: { marketing: { consent: 'SUBSCRIBED' } } }
              }
            }
          }
        },
        relationships: {
          list: { data: { type: 'list', id: this.config.listId } }
        }
      }
    };

    return window
      .fetch(KLAVIYO_ENDPOINT + '?company_id=' + encodeURIComponent(this.config.companyId), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/vnd.api+json',
          revision: KLAVIYO_REVISION
        },
        body: JSON.stringify(payload)
      })
      .catch(function (error) {
        /* fetch rejects ONLY when the request never completed — offline, DNS,
           a refused preflight, or a content blocker dropping it on the floor.
           That is the one failure the first-party fallback is allowed to
           rescue, so tag it HERE, where it is still distinguishable. Once
           there is a Response at all, even a 400, the server answered and
           this is not that case.

           This .catch sits BEFORE the .then on purpose: placed after, it
           would also swallow the not-ok throw below and every Klaviyo
           rejection would masquerade as a blocked request. */
        var unreachable = new Error(
          'Klaviyo request did not complete: ' + ((error && error.message) || 'unknown')
        );
        unreachable.mpUnreachable = true;
        throw unreachable;
      })
      .then(function (response) {
        /* 202 with an empty body is the documented success. Anything else
           is a failure even though fetch itself resolved — fetch only
           rejects on network errors, so a 400 would otherwise sail
           through as a success. */
        if (!response.ok) {
          throw new Error('Klaviyo responded ' + response.status);
        }
        return true;
      });
  };

  /* The store's own domain — the same endpoint, and the same form_type, that
     the theme's footer newsletter form has always posted to. Nothing a
     shopper's browser blocks can reach it, which is the entire point.

     Two callers with two different contracts:
       - after a Klaviyo success: fire-and-forget, the caller swallows
       - as the fallback: AWAITED, and its verdict alone decides whether the
         shopper is told they subscribed
     so this returns a promise that genuinely rejects rather than one that
     resolves whatever happened. */
  MPSignupPill.prototype.createShopifyCustomer = function (email, isFallback) {
    var tags = this.config.customerTags;
    if (isFallback) {
      /* Tag the rescued signups so the size of this is countable in the
         Shopify admin. Shopify's analytics has no browser dimension, so
         without a tag there is no way to find out how many shoppers a
         blocker would otherwise have cost. */
      tags = tags + ', blocked-fallback';
    }

    var body = new FormData();
    body.append('form_type', 'customer');
    body.append('utf8', '✓');
    body.append('contact[email]', email);
    body.append('contact[tags]', tags);

    return window.fetch('/contact', { method: 'POST', body: body }).then(function (response) {
      /* Shopify answers a scripted POST to this endpoint with a bot
         challenge often enough that it cannot be assumed away: measured
         2026-09-28, a cookieless POST came back 403 with a "Verifying your
         connection..." body. A challenge is not a 2xx, so response.ok is the
         discriminator that matters, and a challenged shopper gets the error
         rather than a thank-you for a signup that never happened. */
      if (!response.ok) {
        window.console.warn('[mp-signup-pill] /contact returned', response.status);
        throw new Error('Shopify responded ' + response.status);
      }
      /* A native customer form POST comes back carrying customer_posted on
         the URL. When Shopify says false outright, believe it. When the
         param is absent do NOT invent a failure — a 2xx with no explicit
         denial is the success shape for this endpoint, and failing closed
         here would tell a shopper who DID subscribe that they had not. */
      if (/customer_posted=false/.test(response.url || '')) {
        throw new Error('Shopify rejected the address');
      }
      return true;
    });
  };

  MPSignupPill.prototype.setSubmitting = function (state) {
    this.isSubmitting = state;
    if (this.submit) this.submit.disabled = state;
    if (this.input) this.input.disabled = state;
  };

  MPSignupPill.prototype.showError = function (which) {
    if (!this.errorEl) return;
    this.errorEl.textContent = this.messages[which] || this.messages.generic;
    this.errorEl.hidden = false;
    if (this.input) {
      /* aria-invalid is a statement about the VALUE, so only the
         validation failure earns it. A Klaviyo outage does not make the
         address the shopper typed invalid. */
      this.input.setAttribute('aria-invalid', which === 'invalid' ? 'true' : 'false');
    }
  };

  MPSignupPill.prototype.clearError = function () {
    if (!this.errorEl) return;
    this.errorEl.hidden = true;
    if (this.input) this.input.setAttribute('aria-invalid', 'false');
  };

  MPSignupPill.prototype.setSmsSubmitting = function (state) {
    this.isSubmitting = state;
    if (this.smsSubmit) this.smsSubmit.disabled = state;
    if (this.phone) this.phone.disabled = state;
    /* The skip stays live while the request is in flight on purpose: a
       shopper who changes their mind mid-spinner must not be trapped on a
       screen they are allowed to leave. */
  };

  MPSignupPill.prototype.showSmsError = function (which) {
    if (!this.smsErrorEl) return;
    this.smsErrorEl.textContent = this.messages[which] || this.messages.generic;
    this.smsErrorEl.hidden = false;
    if (this.phone) {
      this.phone.setAttribute('aria-invalid', which === 'phoneInvalid' ? 'true' : 'false');
    }
  };

  /* -----------------------------------------------------------------------
     Phone field formatting.

     Two handlers, because a mask needs both: one to reformat after the value
     changes, and one to stop Backspace being swallowed by the characters the
     mask itself inserted.
     ----------------------------------------------------------------------- */

  /* Backspace with the caret sitting just after a separator deletes the
     separator, the mask immediately puts it back, and the field appears
     frozen — press the key, nothing happens. Walk the caret back over any
     separators FIRST, so the browser's own deletion lands on the digit the
     shopper was actually aiming at. Only for a collapsed caret: a selection
     already covers exactly what they meant to remove. */
  MPSignupPill.prototype.onPhoneKeydown = function (event) {
    if (event.key !== 'Backspace') return;

    var el = this.phone;
    if (!el || el.value.charAt(0) === '+') return;

    var start = el.selectionStart;
    var end = el.selectionEnd;
    if (typeof start !== 'number' || start !== end || start === 0) return;

    var pos = start;
    while (pos > 0 && !/[0-9]/.test(el.value.charAt(pos - 1))) pos--;
    if (pos === start) return;

    el.setSelectionRange(pos, pos);
  };

  MPSignupPill.prototype.onPhoneInput = function () {
    var el = this.phone;
    if (!el) return;

    this.clearSmsError();

    var raw = el.value;

    /* An explicit country code is the shopper saying this is not a NANP
       number, and normalisePhone() accepts +11..15 digits. Forcing it into
       a 10-digit shape would corrupt a number we would otherwise send
       correctly, so the mask stands down entirely. */
    if (raw.charAt(0) === '+') return;

    var formatted = formatNanpPhone(raw);
    if (formatted === null || formatted === raw) return;

    /* Anchor the caret to a DIGIT COUNT, not to a string index: the
       reformat inserts and removes separators around it, so an index taken
       before the rewrite points somewhere else afterwards. */
    var caret = typeof el.selectionStart === 'number' ? el.selectionStart : raw.length;
    var digitsBefore = countDigits(raw.slice(0, caret));
    var digitsTotal = countDigits(formatted);

    el.value = formatted;

    if (typeof el.setSelectionRange !== 'function') return;

    /* Typing at the end is the overwhelmingly common case, and there the
       caret belongs after the separators too — park it at the end of the
       string rather than between the last digit and a trailing ") ". */
    if (digitsBefore >= digitsTotal) {
      el.setSelectionRange(formatted.length, formatted.length);
      return;
    }

    var pos = 0;
    var seen = 0;
    while (pos < formatted.length && seen < digitsBefore) {
      if (/[0-9]/.test(formatted.charAt(pos))) seen++;
      pos++;
    }
    el.setSelectionRange(pos, pos);
  };

  MPSignupPill.prototype.clearSmsError = function () {
    if (!this.smsErrorEl) return;
    this.smsErrorEl.hidden = true;
    if (this.phone) this.phone.setAttribute('aria-invalid', 'false');
  };

  MPSignupPill.prototype.showSuccess = function () {
    var self = this;
    this.step = 3;
    if (this.formState) this.formState.hidden = true;
    if (this.smsState) this.smsState.hidden = true;
    if (this.successState) this.successState.hidden = false;
    if (this.badge) this.badge.hidden = false;

    /* Move focus to the thank-you so a screen reader lands on it rather
       than on a now-hidden input. The heading carries tabindex="-1". */
    var heading = this.root.querySelector('[data-mp-pill-success-heading]');
    if (heading && typeof heading.focus === 'function') heading.focus();

    /* The auto-close SNOOZES rather than dismisses (2026-10-07). The saved
       setting on this store is 6 seconds, and six seconds is not long enough
       to read a discount code, let alone use it — dismissing would take the
       code off the screen with no way back to it. Snoozing leaves the edge
       tab, so the code is one tap away for the rest of the session. Same
       principle as the X and as ESC on this step: after 2026-10-07 the ONLY
       thing that takes the offer away is the shopper saying so in words. */
    if (this.config.successAutoclose > 0) {
      window.setTimeout(function () {
        if (!self.isOpen) return;
        /* Hand focus to the pill ONLY if it is still inside the card we are
           about to hide — showSuccess() put it on the thank-you heading, and
           focus on a display:none element is focus thrown back to <body>. If
           the shopper has tabbed away in the meantime, leave them alone:
           yanking focus out of the page they moved to is worse than the
           thing this guard is protecting against. */
        var inside = !!(self.card && self.card.contains(document.activeElement));
        if (self.config.allowSnooze) {
          self.snooze({ focus: inside });
        } else {
          self.dismiss({ via: 'autoclose' });
        }
      }, this.config.successAutoclose);
    }
  };

  MPSignupPill.prototype.destroy = function () {
    this.stopWobble();
    this.stopSnoozeNudge();
    this.stopScrollWatch();
    if (this.resizeTimer) {
      window.clearTimeout(this.resizeTimer);
      this.resizeTimer = null;
    }
    if (this.suppressClickTimer) {
      window.clearTimeout(this.suppressClickTimer);
      this.suppressClickTimer = null;
    }
    if (this.onPointerMove) {
      window.removeEventListener('pointermove', this.onPointerMove);
      window.removeEventListener('pointerup', this.onPointerUp);
      window.removeEventListener('pointercancel', this.onPointerUp);
    }
    if (this.onResize) window.removeEventListener('resize', this.onResize);
    if (this.onKeydown) document.removeEventListener('keydown', this.onKeydown);
    if (this.onViewport && window.visualViewport) {
      window.visualViewport.removeEventListener('resize', this.onViewport);
    }
  };

  /* ---------------------------------------------------------------------
     Init. Guarded per element so a theme-editor re-render cannot
     double-bind, and re-run on shopify:section:load for the same reason.
     --------------------------------------------------------------------- */
  /* An escape hatch for previewing, added with the storage change on
     2026-10-07. The signup record is now permanent and no longer answers to
     the "Remember that it was closed" setting, which is right for shoppers
     and awkward for whoever is testing the form: one real submit and the
     pill is gone from that browser for good.

     Load any page with ?mp-pill-reset and all three records are cleared, so
     the next load starts from nothing. Read once, not per section, and it
     only ever deletes — there is no state it can put the storefront into
     that a shopper could not reach themselves. */
  function maybeReset() {
    if (window.location.search.indexOf('mp-pill-reset') === -1) return;
    removeStore(STORE_DISMISSED);
    removeStore(STORE_SUBSCRIBED);
    clearSnooze();
    clearScoped(STORE_AUTOEXPANDED);
    window.console.info('[mp-signup-pill] ' + VERSION + ' — stored state cleared');
  }

  function init(scope) {
    var root = scope && scope.querySelectorAll ? scope : document;
    var nodes = Array.prototype.slice.call(root.querySelectorAll('[data-mp-signup-pill]'));

    nodes.forEach(function (node) {
      if (node.mpSignupPill) {
        node.mpSignupPill.destroy();
      }
      node.mpSignupPill = new MPSignupPill(node);

      var cssVersion = window
        .getComputedStyle(node)
        .getPropertyValue('--mp-pill-css-v')
        .trim()
        .replace(/['"]/g, '');
      /* Cross-file stamp. A version declared only inside this file proves
         nothing about the stylesheet — the two are uploaded separately and
         either can go stale on its own. Compare them out loud. */
      if (cssVersion !== VERSION) {
        window.console.warn(
          '[mp-signup-pill] version mismatch — js ' + VERSION + ', css ' + (cssVersion || 'absent')
        );
      }
    });
  }

  maybeReset();

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      init(document);
    });
  } else {
    init(document);
  }

  document.addEventListener('shopify:section:load', function (event) {
    init(event.target || document);
  });
})();
